<script>
  import { onMount, onDestroy } from "svelte";
  import { fetchWithSession } from "@utils/fetchWithSession.js";
  import Button from "../../../../lib/components/Button.svelte";
  import { Checkbox, Pagination, Tag } from "carbon-components-svelte";
  import { TextInput, InfoBlock, Select } from "paxray-components-svelte";
  import EditIcon from "../../settings/svg/EditIcon.svelte";
  import RemoveIcon from "../../settings/svg/RemoveIcon.svelte";
  import PlusIcon from "@components/common/icons/app/PlusIcon.svelte";
  import Checkmark16 from "carbon-icons-svelte/lib/Checkmark16";

  export let listOfDepartments = [];
  export let listOfProcesses = [];
  export let listOfSubprocesses = [];
  export let listOfTasks = [];
  export let selectedDepartment = "";
  export let selectedProcess = "";
  export let selectedSubprocess = "";
  export let selectedTask = "";
  export let filterKey = 0;
  export let companyFrameworkPagedRows = [];
  export let visibleCompanyFrameworkRows = [];
  export let companyFrameworkIssues = [];
  export let companyFrameworkSearch = "";
  export let companyFrameworkCompleteness = "";
  export let companyFrameworkSortKey = "";
  export let companyFrameworkSortDirection = "none";
  export let companyFrameworkPage = 1;
  export let companyFrameworkPageSize = 25;
  export let handleOpenImport = () => {};
  export let showImportButton = false;
  export let resetFilters = () => {};
  export let openCompanyFrameworkAdd = () => {};
  export let openCompanyFrameworkMissingPath = () => {};
  export let toggleCompanyDepartment = () => {};
  export let editFrameworkElement = () => {};
  export let deleteFrameworkElement = () => {};
  export let projectId = "";
  export let companyFrameworkRows = [];
  export let collectionSelectedCount = 0;
  export let collectionStorageError = "";
  export let teamAssignment = false;
  export let unassignedSubtaskIds = new Set();
  export let assignmentFilter = "";
  export let listOfTeams = [];
  export let selectedTeam = "";

  export let collectionReady = false;
  export let collectionSaving = false;
  export let defaultDepartmentIds = [];
  // Erst vorauswaehlen, wenn das Framework (inkl. nachgeladener Org-Departments) komplett da ist - sonst nur Teilmenge
  export let defaultScopeReady = true;
  let mounted = false;
  let defaultScopeApplied = false;
  let loadedScopeKey;
  let collectionRequest = 0;
  let collectionSubtaskIds = [];

  onMount(() => {
    mounted = true;
  });
  onDestroy(() => {
    collectionRequest += 1;
  });

  $: if (mounted && loadedScopeKey !== `${teamAssignment}:${projectId}`) {
    loadedScopeKey = `${teamAssignment}:${projectId}`;
    loadCollection(projectId, teamAssignment);
  }

  $: availableSubtaskIds = new Set(
    companyFrameworkRows
      .filter((row) => row.subtask)
      .map((row) => String(row.subtask.id)),
  );
  $: selectedCollectionIds = new Set(
    collectionSubtaskIds.filter((id) => availableSubtaskIds.has(id)),
  );
  $: collectionSelectedCount = selectedCollectionIds.size;
  $: collectionGroupStates = getCollectionGroupStates(
    companyFrameworkRows,
    selectedCollectionIds,
  );
  // Leerer Scope: Subtasks der Projekt-Departments einmal vorauswaehlen. Ohne Subtask laesst sich Setup nicht
  // abschliessen, ein leerer Scope ist also nie gewollt.
  $: defaultSubtaskIds = companyFrameworkRows
    .filter(
      (row) =>
        row.subtask &&
        defaultDepartmentIds.includes(String(row.department?.id)),
    )
    .map((row) => String(row.subtask.id));
  $: if (
    defaultScopeReady &&
    collectionReady &&
    !collectionSaving &&
    !defaultScopeApplied &&
    !collectionSubtaskIds.length &&
    defaultSubtaskIds.length
  ) {
    defaultScopeApplied = true;
    updateCollection(defaultSubtaskIds, true);
  }
  // Im Projekt neu angelegte Subtasks direkt in den Scope uebernehmen - wer sie hier anlegt, will sie erfassen.
  // Bekannte Subtasks werden einmalig nach dem Laden gemerkt, spaeter abgewaehlte bleiben abgewaehlt.
  let knownSubtaskIds = null;
  let pendingScopeIds = [];
  $: if (projectId && !teamAssignment && defaultScopeReady && collectionReady) trackNewSubtasks(availableSubtaskIds);
  $: if (pendingScopeIds.length && collectionReady && !collectionSaving) {
    const ids = pendingScopeIds;
    pendingScopeIds = [];
    updateCollection(ids, true);
  }

  function trackNewSubtasks(ids) {
    if (!knownSubtaskIds) {
      knownSubtaskIds = new Set(ids);
      return;
    }
    const added = [...ids].filter((id) => !knownSubtaskIds.has(id));
    if (!added.length) return;
    added.forEach((id) => knownSubtaskIds.add(id));
    pendingScopeIds = [...pendingScopeIds, ...added];
  }

  async function readCollectionResponse(response) {
    const data = await response.json();
    if (!response.ok)
      throw new Error(
        data.message || "Collection scope could not be saved or loaded.",
      );
    if (
      !Array.isArray(data.subtask_ids) ||
      data.subtask_ids.some((id) => typeof id !== "string")
    ) {
      throw new Error("Invalid collection scope response.");
    }
    return [...new Set(data.subtask_ids)];
  }

  async function loadCollection(id, forTeam) {
    const requestId = ++collectionRequest;
    collectionSubtaskIds = [];
    collectionStorageError = "";
    collectionReady = false;
    collectionSaving = false;
    defaultScopeApplied = false;
    knownSubtaskIds = null;
    pendingScopeIds = [];
    if (!id || forTeam) return;
    try {
      const ids = await readCollectionResponse(
        await fetchWithSession(`/api/damprojects/project-collection/${id}`, {
          cache: "no-store",
        }),
      );
      if (requestId !== collectionRequest) return;
      collectionSubtaskIds = ids;
      collectionReady = true;
    } catch (error) {
      if (requestId !== collectionRequest) return;
      collectionStorageError = `${error.message || "Collection scope could not be loaded."} Reload the page to try again.`;
    }
  }

  function getCollectionGroupStates(rows, selectedIds) {
    const states = new Map();
    rows.forEach((row) => {
      if (!row.subtask) return;
      ["department", "process", "subprocess", "task"].forEach((level) => {
        if (!row[level]) return;
        const key = `${level}:${row[level].id}`;
        if (!states.has(key)) states.set(key, { ids: new Set() });
        states.get(key).ids.add(String(row.subtask.id));
      });
    });
    states.forEach((state) => {
      const selectedCount = [...state.ids].filter((id) =>
        selectedIds.has(id),
      ).length;
      state.selectedCount = selectedCount;
      state.checked = selectedCount === state.ids.size;
      state.indeterminate = selectedCount > 0 && !state.checked;
    });
    return states;
  }

  async function updateCollection(ids, checked) {
    if (!collectionReady || collectionSaving || !projectId || teamAssignment)
      return;
    const id = projectId;
    const requestId = collectionRequest;
    const previous = collectionSubtaskIds;
    const next = new Set(previous);
    const changedIds = [...new Set(ids.map(String))];
    changedIds.forEach((subtaskId) =>
      checked ? next.add(subtaskId) : next.delete(subtaskId),
    );
    collectionSubtaskIds = [...next];
    collectionSaving = true;
    collectionStorageError = "";
    try {
      const saved = await readCollectionResponse(
        await fetchWithSession(`/api/damprojects/project-collection/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            add_subtask_ids: checked ? changedIds : [],
            remove_subtask_ids: checked ? [] : changedIds,
          }),
        }),
      );
      if (requestId !== collectionRequest) return;
      collectionSubtaskIds = saved;
    } catch (error) {
      if (requestId !== collectionRequest) return;
      collectionSubtaskIds = previous;
      collectionReady = false;
      collectionStorageError = `${error.message || "Collection scope could not be saved."} Reload the page to check the saved selection.`;
    } finally {
      if (requestId === collectionRequest) collectionSaving = false;
    }
  }

  const columns = [
    {
      key: "department",
      name: "Department",
      type: "Departments",
      label: "Department (L0)",
    },
    {
      key: "process",
      name: "Process",
      type: "Processes",
      label: "Process (L1)",
      parent: "department",
    },
    {
      key: "subprocess",
      name: "Subprocess",
      type: "Subprocesses",
      label: "Subprocess (L2)",
      parent: "process",
    },
    {
      key: "task",
      name: "Task",
      type: "Tasks",
      label: "Task (L3)",
      parent: "subprocess",
    },
    {
      key: "subtask",
      name: "Subtask",
      type: "Subtasks",
      label: "Subtask (L4)",
      parent: "task",
    },
  ];
  const assignmentFilterOptions = [
    { value: "unassigned", text: "Missing" },
    { value: "assigned", text: "Assigned" },
  ];
  const completenessFilterOptions = [
    { value: "", text: "All" },
    { value: "incomplete", text: "Incomplete" },
  ];

  $: departmentFilterItems = listOfDepartments.map((option) => ({
    value: option.key,
    text: option.value,
  }));
  $: processFilterItems = listOfProcesses.map((option) => ({
    value: option.key,
    text: option.value,
  }));
  $: subprocessFilterItems = listOfSubprocesses.map((option) => ({
    value: option.key,
    text: option.value,
  }));
  $: taskFilterItems = listOfTasks.map((option) => ({
    value: option.key,
    text: option.value,
  }));
  $: teamFilterItems = listOfTeams.map((option) => ({
    value: option.key,
    text: option.value,
  }));
  // Im Projekt (und beim Team-Assignment) ohne Department-Spalte, gruppiert nach Process - Departments lassen
  // sich dort weder bearbeiten noch loeschen
  $: groupByProcess = teamAssignment || Boolean(projectId);
  $: groupLevel = groupByProcess ? "process" : "department";
  $: groupFallback = groupByProcess
    ? "Unassigned process"
    : "Unassigned department";
  $: visibleColumns = groupByProcess
    ? columns.filter((column) => column.key !== "department")
    : columns;
  // "Add" einer Ebene erst anbieten, wenn es die uebergeordnete Ebene gibt, sonst ist der neue Eintrag nicht zuordenbar
  $: addableColumnKeys = new Set(
    visibleColumns
      .filter(
        (column, index) =>
          index === 0 ||
          companyFrameworkRows.some((row) => row[column.parent]),
      )
      .map((column) => column.key),
  );
  $: firstColumn = visibleColumns[0];
  $: frameworkIsEmpty = !companyFrameworkRows.some(
    (row) => row[firstColumn?.key],
  );
  // Elemente unter Departments mit zugewiesenen Projekten duerfen nicht geloescht werden (alle Ebenen im Pfad)
  $: lockedFrameworkIds = projectId
    ? new Set()
    : getLockedFrameworkIds(companyFrameworkRows);
  function getLockedFrameworkIds(rows) {
    const locked = new Set();
    rows.forEach((row) => {
      if (!row.department?.projects?.length) return;
      ["department", "process", "subprocess", "task", "subtask"].forEach(
        (level) => {
          if (row[level]) locked.add(`${level}:${row[level].id}`);
        },
      );
    });
    return locked;
  }
  $: assignmentGroupStates = getAssignmentGroupStates(
    companyFrameworkRows,
    unassignedSubtaskIds,
    groupLevel,
  );
  $: levelSubtaskIds = getLevelSubtaskIds(companyFrameworkRows);

  // Process, Subprocess und Task werden ueber ihre L4 zugewiesen (Subtask-Chip bewusst redundant zur Teams-Spalte), inklusive gefilterter Zeilen und anderer Seiten
  function getLevelSubtaskIds(rows) {
    const ids = new Map();
    if (!teamAssignment) return ids;
    rows.forEach((row) => {
      if (!row.subtask) return;
      ["process", "subprocess", "task", "subtask"].forEach((level) => {
        if (!row[level]) return;
        const key = `${level}:${row[level].id}`;
        if (!ids.has(key)) ids.set(key, new Set());
        ids.get(key).add(String(row.subtask.id));
      });
    });
    return ids;
  }

  function getAssignmentGroupStates(rows, unassignedIds, level) {
    const states = new Map();
    if (!teamAssignment) return states;
    rows.forEach((row) => {
      if (!row.subtask) return;
      const key = row[level]?.id || "unassigned";
      if (!states.has(key))
        states.set(key, { ids: new Set(), missing: new Set() });
      const id = String(row.subtask.id);
      states.get(key).ids.add(id);
      if (unassignedIds.has(id)) states.get(key).missing.add(id);
    });
    return states;
  }

  function measureMissingLabels(node) {
    const labels = node.querySelectorAll(".missing-labels > span");
    const updateWidths = () => {
      node.style.setProperty(
        "--missing-width",
        getComputedStyle(labels[0]).width,
      );
      node.style.setProperty("--add-width", getComputedStyle(labels[1]).width);
    };
    const observer = new ResizeObserver(updateWidths);
    labels.forEach((label) => observer.observe(label));
    updateWidths();
    return { destroy: () => observer.disconnect() };
  }

  function toggleSort(key) {
    companyFrameworkSortDirection =
      companyFrameworkSortKey !== key ||
      companyFrameworkSortDirection === "none"
        ? "ascending"
        : companyFrameworkSortDirection === "ascending"
          ? "descending"
          : "none";
    companyFrameworkSortKey =
      companyFrameworkSortDirection === "none" ? "" : key;
  }
</script>

<div class="company-framework-content" class:team-assignment={teamAssignment}>
  {#if !projectId && !teamAssignment}
    <div class="company-framework-intro">
      <InfoBlock
        type="info"
        heading="Process Framework"
        showClose={true}
        message="Define how work is structured across your company, from processes to individual tasks and subtasks."
      />
    </div>
  {/if}
  {#if companyFrameworkIssues.length}
    <div class="company-framework-notices">
      {#each companyFrameworkIssues as issue (issue.type)}
        <InfoBlock
          type="warning"
          heading={issue.type}
          showClose={true}
          message={[
            issue.missingProjects
              ? `${issue.missingProjects} not linked to the current project.`
              : "",
            issue.missingParents
              ? `${issue.missingParents} without a linked parent.`
              : "",
            issue.missingChildren
              ? `${issue.missingChildren} without child entries.`
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        />
      {/each}
    </div>
  {/if}

  <!-- Im Projekt ohne Eintraege keine Filterleiste, nur der Hinweis der Seite -->
  {#if !projectId || companyFrameworkRows.length || showImportButton}
    <div class="company-framework-filter-bar">
      {#if !projectId || companyFrameworkRows.length}
        <div class="company-framework-search">
          <TextInput
            id="company-framework-search"
            type="search"
            placeholder="Search framework…"
            bind:value={companyFrameworkSearch}
          />
        </div>
        {#key filterKey}
          {#if teamAssignment}
            <Select
              items={assignmentFilterOptions}
              bind:selected={assignmentFilter}
              color="dark"
              placeholder="All coverage"
            />
          {:else}
            <Select
              items={completenessFilterOptions}
              bind:selected={companyFrameworkCompleteness}
              color="dark"
            />
            {#if !projectId}
              <Select
                items={departmentFilterItems}
                bind:selected={selectedDepartment}
                color="dark"
                placeholder="Departments"
              />
            {/if}
          {/if}
          <Select
            items={processFilterItems}
            bind:selected={selectedProcess}
            color="dark"
            placeholder="Processes"
          />
          <Select
            items={subprocessFilterItems}
            bind:selected={selectedSubprocess}
            color="dark"
            placeholder="Subprocesses"
          />
          <Select
            items={taskFilterItems}
            bind:selected={selectedTask}
            color="dark"
            placeholder="Tasks"
          />
          {#if teamAssignment}
            <Select
              items={teamFilterItems}
              bind:selected={selectedTeam}
              color="dark"
              placeholder="Assigned team"
            />
          {/if}
        {/key}
        <button
          type="button"
          class="reset-filter-button"
          title="Reset filters"
          aria-label="Reset filters"
          on:click={resetFilters}
          ><img src="/img/assistant/icons/reset.svg" alt="" /></button
        >
      {/if}
      {#if showImportButton}
        <Button
          variant="secondary"
          setting={true}
          size="sm"
          text="Import Framework (xlsx)"
          on:click={handleOpenImport}
        />
      {/if}
    </div>
  {/if}

  <div class="company-framework-table-wrapper">
    <table class="company-framework-table">
      <thead
        ><tr>
          <th class="company-framework-expand-heading"></th>
          {#each visibleColumns as column}
            <th
              aria-sort={companyFrameworkSortKey === column.key
                ? companyFrameworkSortDirection
                : "none"}
            >
              <div class="company-framework-heading">
                <button
                  type="button"
                  class="company-framework-sort-button"
                  on:click={() => toggleSort(column.key)}
                >
                  {column.label}{#if companyFrameworkSortKey === column.key}<span
                      aria-hidden="true"
                      >{companyFrameworkSortDirection === "ascending"
                        ? "↑"
                        : "↓"}</span
                    >{/if}
                </button>
                {#if !teamAssignment && addableColumnKeys.has(column.key)}<button
                    type="button"
                    class="company-framework-add-button"
                    title={`Add ${column.key}`}
                    aria-label={`Add ${column.key}`}
                    on:click={() => openCompanyFrameworkAdd(column.type)}
                    ><PlusIcon color="currentColor" size={14} />Add</button
                  >{/if}
              </div>
            </th>
          {/each}
          {#if teamAssignment}<th class="team-column" scope="col">Teams</th><th
              class="coverage-column"
              scope="col">Coverage</th
            >
          {:else if projectId}<th
              class="collection-column"
              scope="col"
              title="Include subtasks in this project's collection scope. Changes are saved immediately; an empty selection includes no subtasks."
              >In Scope</th
            >{/if}
        </tr></thead
      >
      <tbody>
        {#if companyFrameworkPagedRows.length}
          {#each companyFrameworkPagedRows as row, rowIndex (row.id)}
            {#if row.isDepartmentStart || rowIndex === 0}
              {@const assignmentState = assignmentGroupStates.get(
                row.departmentKey,
              )}
              <tr
                class="company-framework-group"
                class:company-framework-group-expanded={!row.isDepartmentCollapsed}
                class:alternate-group={row.isAlternateGroup}
                class:in-scope={!teamAssignment &&
                  projectId &&
                  row[groupLevel] &&
                  collectionGroupStates.get(
                    `${groupLevel}:${row[groupLevel].id}`,
                  )?.checked}
                class:unassigned={teamAssignment &&
                  assignmentState?.missing.size}
              >
                <td class="company-framework-expand-cell">
                  <button
                    type="button"
                    class="company-framework-expand-button"
                    aria-expanded={!row.isDepartmentCollapsed}
                    aria-label={`${row.isDepartmentCollapsed ? "Expand" : "Collapse"} ${row[groupLevel]?.name || groupFallback}`}
                    on:click={() => toggleCompanyDepartment(row.departmentKey)}
                    ><span
                      class:collapsed={row.isDepartmentCollapsed}
                      class="tree-chevron"
                      aria-hidden="true"
                    ></span></button
                  >
                </td>
                {#each visibleColumns as column}
                  <td>
                    <div class="company-framework-group-summary">
                      {#if column.key === groupLevel}
                        {#if !teamAssignment && projectId && row[groupLevel]}
                          {@const selection = collectionGroupStates.get(
                            `${groupLevel}:${row[groupLevel].id}`,
                          )}
                          {#if selection}
                            <Checkbox
                              disabled={!collectionReady || collectionSaving}
                              checked={selection.checked}
                              indeterminate={selection.indeterminate}
                              hideLabel
                              labelText={`Include all subtasks in project scope for ${column.name}: ${row[groupLevel].name}`}
                              title={`Select all ${selection.ids.size} subtasks in ${row[groupLevel].name}, including collapsed, filtered-out rows and other pages`}
                              on:change={(event) =>
                                updateCollection(
                                  [...selection.ids],
                                  event.target.checked,
                                )}
                            />
                          {/if}
                        {/if}
                        <button
                          type="button"
                          class="company-framework-group-name"
                          title={row[groupLevel]?.name || groupFallback}
                          aria-expanded={!row.isDepartmentCollapsed}
                          on:click={() =>
                            toggleCompanyDepartment(row.departmentKey)}
                          >{row[groupLevel]?.name || groupFallback}</button
                        >
                        {#if !teamAssignment && row.departmentSummary.missing.some((missing) => missing.level === groupLevel)}
                          <span
                            class="company-framework-missing"
                            use:measureMissingLabels
                          >
                            <Tag
                              type="red"
                              interactive
                              aria-label={`No ${column.name}; Add ${column.name}`}
                              on:click={() =>
                                openCompanyFrameworkMissingPath(
                                  column.key,
                                  row,
                                )}
                            >
                              <span class="missing-labels" aria-hidden="true">
                                <span class="missing-status"
                                  >No {column.name}</span
                                >
                                <span class="missing-action"
                                  ><PlusIcon
                                    color="currentColor"
                                    size={14}
                                  />Add {column.name}</span
                                >
                              </span>
                            </Tag>
                          </span>
                        {/if}
                      {:else if teamAssignment && row.departmentSummary.counts[column.key] === 0}
                        <span>−</span>
                      {:else if row.departmentSummary.counts[column.key] === 0}
                        <!-- Gruppenzeile nur als Status, Anlegen erfolgt in den Detailzeilen darunter -->
                        <Tag type="red">No {column.name}</Tag>
                      {:else}
                        <span
                          class="company-framework-group-counts"
                          title={companyFrameworkCompleteness === "incomplete"
                            ? "Unique elements in the matching incomplete paths"
                            : "Unique elements in the entire department, including filtered-out entries"}
                        >
                          {row.departmentSummary.counts[column.key]}
                          {row.departmentSummary.counts[column.key] === 1
                            ? column.name
                            : column.type}
                        </span>
                      {/if}
                      {#each row.departmentSummary.missing.filter((missing) => missing.level === column.key && column.key !== groupLevel && row.departmentSummary.counts[column.key] > 0) as missing}
                        <Tag
                          type="red"
                          title={`${missing.count} missing ${column.name}`}
                          aria-label={`${missing.count} missing ${column.name}`}
                          >{missing.count} Missing</Tag
                        >
                      {/each}
                    </div>
                  </td>
                {/each}
                {#if teamAssignment}
                  <td class="team-column">
                    <slot
                      name="team-assignment"
                      targetIds={assignmentState
                        ? [...assignmentState.ids]
                        : []}
                      label={row[groupLevel]?.name || groupFallback}
                      group={true}
                      level={groupLevel}
                      variant="select"
                    />
                  </td>
                  <td class="coverage-column"
                    ><span class="assignment-status"
                      >{#if assignmentState?.missing.size}<span
                          class="coverage-chip missing"
                          >{assignmentState.ids.size -
                            assignmentState.missing.size}/{assignmentState.ids
                            .size}</span
                        >{:else}<span
                          class="coverage-chip complete"
                          title={`All ${assignmentState?.ids.size || 0} subtasks assigned`}
                          aria-label={`All ${assignmentState?.ids.size || 0} subtasks assigned`}
                          ><Checkmark16 /></span
                        >{/if}</span
                    ></td
                  >
                {:else if projectId}
                  <td class="collection-column">
                    {#if row[groupLevel]}
                      {@const selection = collectionGroupStates.get(
                        `${groupLevel}:${row[groupLevel].id}`,
                      )}
                      <span
                        class="collection-scope-tag"
                        title={`${selection?.selectedCount || 0} of ${selection?.ids.size || 0} subtasks in scope, including filtered-out rows and other pages`}
                      >
                        {#if selection?.selectedCount}
                          <Tag type="green"
                            >{selection.checked
                              ? "All"
                              : `${selection.selectedCount}/${selection.ids.size}`}</Tag
                          >
                        {:else}
                          <span aria-label="No subtasks selected">−</span>
                        {/if}
                      </span>
                    {/if}
                  </td>
                {/if}
              </tr>
            {/if}
            {#if !row.isDepartmentCollapsed}
              <tr
                class="company-framework-detail-row"
                class:incomplete={!projectId &&
                  !teamAssignment &&
                  columns.some((column) => !row[column.key])}
                class:alternate-group={row.isAlternateGroup}
                class:in-scope={!teamAssignment &&
                  projectId &&
                  row.subtask &&
                  selectedCollectionIds.has(String(row.subtask.id))}
                class:unassigned={teamAssignment &&
                  row.subtask &&
                  unassignedSubtaskIds.has(String(row.subtask.id))}
              >
                <td class="company-framework-expand-cell"></td>
                {#each visibleColumns as column}
                  <td>
                    {#if row[column.key]}
                      <div class="company-framework-cell">
                        <div class="company-framework-cell-title">
                          {#if !teamAssignment && projectId && ["process", "subprocess", "task"].includes(column.key)}
                            {@const selection = collectionGroupStates.get(
                              `${column.key}:${row[column.key].id}`,
                            )}
                            {#if selection}
                              <Checkbox
                                disabled={!collectionReady || collectionSaving}
                                checked={selection.checked}
                                indeterminate={selection.indeterminate}
                                hideLabel
                                labelText={`Include all subtasks in project scope for ${column.name}: ${row[column.key].name}`}
                                title={`Select all ${selection.ids.size} subtasks in ${row[column.key].name}, including other pages and filtered-out rows`}
                                on:change={(event) =>
                                  updateCollection(
                                    [...selection.ids],
                                    event.target.checked,
                                  )}
                              />
                            {/if}
                          {/if}
                          <span
                            class="company-framework-cell-name"
                            title={row[column.key].name}
                            >{row[column.key].name}</span
                          >
                          {#if teamAssignment && levelSubtaskIds.has(`${column.key}:${row[column.key].id}`)}
                            <span class="company-framework-team-chip">
                              <slot
                                name="team-assignment"
                                targetIds={[
                                  ...levelSubtaskIds.get(
                                    `${column.key}:${row[column.key].id}`,
                                  ),
                                ]}
                                label={row[column.key].name}
                                group={true}
                                level={column.key}
                                variant="chip"
                              />
                            </span>
                          {/if}
                        </div>
                        {#if !teamAssignment}<div
                            class="company-framework-cell-actions"
                          >
                            <!-- Plus legt ein Element derselben Ebene an, verknuepft mit dem Elternelement dieser Zeile -->
                            {#if column.parent && row[column.parent]}
                              <button
                                type="button"
                                class="company-framework-action-button"
                                title={`Add ${column.key} to ${row[column.parent].name}`}
                                aria-label={`Add ${column.key} to ${row[column.parent].name}`}
                                on:click={() =>
                                  openCompanyFrameworkAdd(column.type, row)}
                                ><PlusIcon size={16} /></button
                              >
                            {/if}
                            <button
                              type="button"
                              class="company-framework-action-button"
                              title={`Edit ${column.key}: ${row[column.key].name}`}
                              aria-label={`Edit ${column.key}: ${row[column.key].name}`}
                              on:click={() =>
                                editFrameworkElement(
                                  column.type,
                                  row[column.key],
                                )}><EditIcon /></button
                            >
                            <button
                              type="button"
                              class="company-framework-action-button"
                              disabled={lockedFrameworkIds.has(
                                `${column.key}:${row[column.key].id}`,
                              )}
                              title={lockedFrameworkIds.has(
                                `${column.key}:${row[column.key].id}`,
                              )
                                ? `Cannot delete ${column.key}: used by a project`
                                : `Delete ${column.key}: ${row[column.key].name}`}
                              aria-label={`Delete ${column.key}: ${row[column.key].name}`}
                              on:click={() =>
                                deleteFrameworkElement(
                                  column.type,
                                  row[column.key],
                                )}><RemoveIcon /></button
                            >
                          </div>{/if}
                      </div>
                    {:else if teamAssignment}
                      <span>−</span>
                    {:else}
                      <span
                        class="company-framework-missing"
                        use:measureMissingLabels
                      >
                        <Tag
                          type="red"
                          interactive
                          aria-label={`No ${column.name}; Add ${column.name}`}
                          on:click={() =>
                            openCompanyFrameworkMissingPath(column.key, row)}
                        >
                          <span class="missing-labels" aria-hidden="true">
                            <span class="missing-status">No {column.name}</span>
                            <span class="missing-action"
                              ><PlusIcon color="currentColor" size={14} />Add {column.name}</span
                            >
                          </span>
                        </Tag>
                      </span>
                    {/if}
                  </td>
                {/each}
                {#if teamAssignment}
                  <td class="team-column">
                    <slot
                      name="team-assignment"
                      targetIds={row.subtask ? [String(row.subtask.id)] : []}
                      label={row.subtask?.name || "Subtask"}
                      group={false}
                      level="subtask"
                      variant="select"
                    />
                  </td>
                  <td class="coverage-column"
                    ><span class="assignment-status"
                      >{#if row.subtask && unassignedSubtaskIds.has(String(row.subtask.id))}<span
                          title="No team assigned"
                          aria-label="No team assigned">−</span
                        >{:else if row.subtask}<span
                          class="coverage-chip complete"
                          title="Team assigned"
                          aria-label="Team assigned"><Checkmark16 /></span
                        >{/if}</span
                    ></td
                  >
                {:else if projectId}
                  <td class="collection-column">
                    {#if row.subtask}
                      <Checkbox
                        disabled={!collectionReady || collectionSaving}
                        checked={selectedCollectionIds.has(
                          String(row.subtask.id),
                        )}
                        hideLabel
                        labelText={`In scope: ${row.subtask.name}`}
                        on:change={(event) =>
                          updateCollection(
                            [row.subtask.id],
                            event.target.checked,
                          )}
                      />
                    {/if}
                  </td>
                {/if}
              </tr>
            {/if}
          {/each}
        {:else}
          <tr
            ><td
              class="company-framework-empty"
              colspan={visibleColumns.length +
                1 +
                (teamAssignment ? 2 : projectId ? 1 : 0)}
              >{#if frameworkIsEmpty && !teamAssignment}<div
                  class="company-framework-empty-state"
                >
                  <span>No {firstColumn.type.toLowerCase()} yet</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="plus"
                    iconLeft={true}
                    text={`Add ${firstColumn.key}`}
                    on:click={() => openCompanyFrameworkAdd(firstColumn.type)}
                  />
                </div>{:else}No framework entries match the selected filters.{/if}</td
            ></tr
          >
        {/if}
      </tbody>
    </table>
    <div class="company-framework-pagination">
      <Pagination
        bind:pageSize={companyFrameworkPageSize}
        bind:page={companyFrameworkPage}
        totalItems={visibleCompanyFrameworkRows.length}
        pageSizes={[10, 25, 50, 75, 100]}
      />
    </div>
  </div>
</div>

<style>
  .company-framework-content.team-assignment {
    min-height: 0;
  }
  .team-assignment .company-framework-filter-bar {
    padding: 0;
  }
  .team-assignment .company-framework-table-wrapper {
    margin: 0;
  }
  .company-framework-table .team-column {
    width: 13rem;
  }
  .company-framework-table .coverage-column {
    width: 8rem;
    text-align: center;
  }
  .coverage-chip {
    display: inline-flex;
    min-height: 1.5rem;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 0.125rem 0.5rem;
    border-radius: 1rem;
    font-size: 0.75rem;
    line-height: 1;
    white-space: nowrap;
  }
  .coverage-chip.missing {
    background: var(--pax-red);
  }
  .coverage-chip.complete {
    background: var(--pax-green);
  }
  .company-framework-table tbody tr.company-framework-detail-row.incomplete td,
  .company-framework-table tbody tr.company-framework-group.unassigned td,
  .company-framework-table tbody tr.company-framework-detail-row.unassigned td {
    background-color: color-mix(
      in srgb,
      var(--pax-red) 35%,
      var(--pax-white) 65%
    ) !important;
  }
  .company-framework-table
    tbody
    tr.company-framework-detail-row.incomplete:hover
    td,
  .company-framework-table tbody tr.company-framework-group.unassigned:hover td,
  .company-framework-table
    tbody
    tr.company-framework-detail-row.unassigned:hover
    td {
    background-color: color-mix(
      in srgb,
      var(--pax-red) 55%,
      var(--pax-white) 45%
    ) !important;
  }
  .company-framework-table .collection-column {
    width: 6rem;
    text-align: center;
  }
  .collection-column :global(.bx--checkbox-wrapper) {
    align-items: center;
    margin: 0;
  }
  .collection-scope-tag :global(.bx--tag) {
    background-color: var(--pax-green);
    color: var(--pax-black);
    margin: 0;
  }
  .company-framework-cell-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }
  .company-framework-cell-title .company-framework-cell-name {
    flex: 1;
    min-width: 0;
  }
  .company-framework-cell-title :global(.bx--checkbox-wrapper) {
    flex: 0 0 auto;
    margin: 0;
  }
  .company-framework-content {
    display: flex;
    width: 100%;
    min-height: calc(100vh - 4rem);
    flex: 1;
    flex-direction: column;
    gap: var(--control-gap);
    background: var(--pax-white);
  }
  .company-framework-filter-bar {
    display: flex;
    flex-wrap: wrap;
    min-height: 2.5rem;
    box-sizing: border-box;
    align-items: center;
    gap: var(--control-gap);
    padding: 0 1rem;
  }
  .company-framework-filter-bar:first-child,
  .company-framework-intro:empty + .company-framework-filter-bar,
  .company-framework-notices:empty + .company-framework-filter-bar {
    margin-top: var(--framework-top-gap, 1rem);
  }
  .company-framework-content.team-assignment
    .company-framework-filter-bar:first-child,
  .company-framework-content.team-assignment
    .company-framework-notices:empty
    + .company-framework-filter-bar {
    margin-top: 0;
  }
  .company-framework-filter-bar :global(.bx--form-item) {
    flex: 1 1 8rem;
    min-width: 0;
  }
  .company-framework-filter-bar .reset-filter-button {
    flex: 0 0 2.5rem;
  }
  .reset-filter-button {
    display: flex;
    width: 2.5rem;
    height: 2.5rem;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 0.5rem;
    background: var(--pax-black);
    cursor: pointer;
  }
  .company-framework-table-wrapper {
    overflow: auto;
    margin: 0 1rem;
    border: 1px solid var(--pax-smoke);
    border-radius: 0.5rem;
    background: var(--pax-white);
  }
  /* Ohne Filterleiste (Projekt ohne Eintraege) denselben Abstand wie zwischen Filter und Tabelle */
  .company-framework-table-wrapper:first-child {
    margin-top: var(--framework-top-gap, var(--control-gap));
  }
  .company-framework-table {
    width: 100%;
    min-width: 72rem;
    table-layout: fixed;
    font-size: 0.8125rem;
    border-right: 1px solid var(--border-color);
    border-left: 1px solid var(--border-color);
    border-collapse: collapse;
  }
  .company-framework-table th,
  .company-framework-table td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--pax-smoke);
    color: var(--pax-black);
    text-align: left;
    vertical-align: middle;
  }
  .company-framework-table th {
    font-size: 0.875rem;
    font-weight: 400;
    background-color: var(--pax-black);
    color: var(--pax-white);
  }
  .company-framework-search {
    flex: 1 1 12rem;
    min-width: 10rem;
  }
  .company-framework-search :global(.text-input) {
    margin-top: 0;
  }
  .company-framework-search :global(input) {
    height: 2.5rem;
    box-sizing: border-box;
  }
  .company-framework-search :global(.label-row) {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  .company-framework-notices {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--control-gap);
    margin: var(--framework-top-gap, 1rem) 1rem 0;
  }
  .company-framework-notices:empty {
    display: none;
  }
  .company-framework-intro {
    margin: var(--framework-top-gap, 1rem) 1rem 0;
  }
  .company-framework-intro:empty {
    display: none;
  }
  .company-framework-intro :global(.warning-notice) {
    margin-bottom: 0;
  }
  .company-framework-intro:not(:empty) + .company-framework-notices {
    margin-top: 0;
  }
  .company-framework-notices :global(.warning-notice) {
    margin-bottom: 0;
  }
  @media (max-width: 48rem) {
    .company-framework-notices {
      grid-template-columns: minmax(0, 1fr);
    }
  }
  .company-framework-heading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .company-framework-sort-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    border: 0;
    background: transparent;
    font: inherit;
    font-weight: 400;
    color: inherit;
    text-align: left;
    cursor: pointer;
  }
  .company-framework-table tbody td {
    background-color: var(--pax-white);
  }
  .company-framework-table tbody tr.alternate-group td {
    background-color: color-mix(
      in srgb,
      var(--pax-smoke) 70%,
      var(--pax-white) 30%
    ) !important;
  }
  .company-framework-table tbody tr.company-framework-detail-row:hover td {
    background-color: var(--pax-smoke) !important;
  }
  .company-framework-table tbody tr.company-framework-detail-row.in-scope td,
  .company-framework-table tbody tr.company-framework-group.in-scope td {
    background-color: color-mix(
      in srgb,
      var(--pax-green) 35%,
      var(--pax-white) 65%
    ) !important;
  }
  .company-framework-table
    tbody
    tr.company-framework-detail-row.in-scope:hover
    td {
    background-color: color-mix(
      in srgb,
      var(--pax-green) 55%,
      var(--pax-white) 45%
    ) !important;
  }
  .company-framework-group-summary {
    display: flex;
    min-width: 0;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 1rem;
  }
  .company-framework-group-summary :global(.bx--checkbox-wrapper) {
    flex: 0 0 auto;
    margin: 0;
  }
  .company-framework-group-name {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--pax-black);
    font: inherit;
    font-weight: 600;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }
  .company-framework-group-name:focus-visible {
    outline: 2px solid var(--pax-black);
    outline-offset: 0.125rem;
  }
  .company-framework-group-counts {
    font-size: 0.8125rem;
  }
  .company-framework-table tbody tr.company-framework-group-expanded td,
  .company-framework-table tbody tr.company-framework-detail-row td {
    border-bottom-color: var(--border-color, #d9d9d9);
  }
  .company-framework-cell {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.5rem;
  }
  .company-framework-cell-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .company-framework-cell-actions {
    display: flex;
    gap: 0.25rem;
  }
  @media (hover: hover) {
    .company-framework-cell {
      gap: 0;
    }
    .company-framework-cell-actions {
      max-height: 0;
      margin-top: 0;
      overflow: hidden;
      opacity: 0;
      pointer-events: none;
      transform: translateY(-0.25rem);
      transition:
        max-height 150ms ease,
        margin-top 150ms ease,
        opacity 150ms ease,
        transform 150ms ease;
    }
    .company-framework-detail-row:hover .company-framework-cell-actions,
    .company-framework-cell:focus-within .company-framework-cell-actions {
      max-height: 2rem;
      margin-top: 0.5rem;
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0);
    }
    /* Team-Chip ueberlagert den Namen nur beim Hover der Zelle, damit die Zeilenhoehe stabil bleibt */
    .company-framework-cell-title {
      position: relative;
    }
    .company-framework-team-chip {
      position: absolute;
      right: 0;
      opacity: 0;
      pointer-events: none;
      transition: opacity 150ms ease;
    }
    .company-framework-cell:hover .company-framework-team-chip,
    .company-framework-cell:focus-within .company-framework-team-chip,
    .company-framework-team-chip:has([aria-expanded="true"]) {
      opacity: 1;
      pointer-events: auto;
    }
  }
  .company-framework-missing {
    display: inline-flex;
  }
  .company-framework-missing :global(button) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: calc(var(--missing-width, 8rem) + 1rem);
    padding: 0 0.5rem;
    transition:
      width 300ms ease,
      background-color 300ms ease,
      color 300ms ease;
  }
  .company-framework-missing :global(button > span) {
    width: 100%;
  }
  .missing-labels {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    place-items: center;
    width: 100%;
  }
  .missing-status,
  .missing-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    grid-area: 1 / 1;
    width: max-content;
    gap: 0.25rem;
    line-height: 1rem;
    white-space: nowrap;
    transition: opacity 300ms ease;
  }
  .missing-action {
    opacity: 0;
  }
  .company-framework-missing :global(button:is(:hover, :focus-visible)) {
    width: calc(var(--add-width, 7rem) + 1rem);
    background: var(--pax-black);
    color: var(--pax-white);
  }
  .company-framework-missing :global(button:focus-visible) {
    outline: 2px solid var(--pax-black);
    outline-offset: 0.125rem;
  }
  .company-framework-missing
    :global(button:is(:hover, :focus-visible) .missing-status) {
    opacity: 0;
  }
  .company-framework-missing
    :global(button:is(:hover, :focus-visible) .missing-action) {
    opacity: 1;
  }
  @media (prefers-reduced-motion: reduce) {
    .company-framework-missing :global(button),
    .missing-status,
    .missing-action {
      transition: none;
    }
  }
  .company-framework-action-button:focus-visible,
  .company-framework-sort-button:focus-visible,
  .company-framework-add-button:focus-visible {
    outline: 2px solid var(--pax-black);
    outline-offset: 0.125rem;
  }
  .company-framework-table thead button:focus-visible {
    outline-color: var(--pax-white);
  }
  .company-framework-expand-heading,
  .company-framework-expand-cell {
    width: 3.5rem;
    padding: 0.5rem !important;
    text-align: center !important;
  }
  .company-framework-expand-button,
  .company-framework-add-button,
  .company-framework-action-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    cursor: pointer;
  }
  .company-framework-add-button {
    gap: 0.25rem;
    height: 1.5rem;
    padding: 0 0.5rem 0 0.375rem;
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 999px;
    color: var(--pax-white);
    font: inherit;
    font-size: 0.75rem;
    transition:
      background-color 0.15s,
      color 0.15s,
      border-color 0.15s;
  }
  .company-framework-add-button:hover {
    border-color: var(--pax-white);
    background: var(--pax-white);
    color: var(--pax-black);
  }
  .company-framework-expand-button {
    width: 1.75rem;
    height: 1.75rem;
  }
  .tree-chevron {
    display: block;
    width: 0.45rem;
    height: 0.45rem;
    border-right: 1.5px solid currentColor;
    border-bottom: 1.5px solid currentColor;
    transform: rotate(45deg);
  }
  .tree-chevron.collapsed {
    transform: rotate(-45deg);
  }
  .company-framework-empty {
    color: var(--pax-sub) !important;
    text-align: center !important;
  }
  .company-framework-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--control-gap);
    padding: 0.5rem 0;
  }
  .company-framework-action-button {
    width: 2rem;
    height: 2rem;
    vertical-align: middle;
    border-radius: 0.35rem;
    background: var(--pax-black);
  }
  .company-framework-action-button:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
  .company-framework-pagination {
    overflow: hidden;
  }
  .company-framework-pagination :global(.bx--pagination) {
    box-sizing: border-box;
    width: 100%;
    min-height: 2.5rem;
    border: 0;
    border-top: 1px solid var(--pax-sub-new);
    background: var(--pax-black);
    color: var(--pax-white);
  }
  .company-framework-pagination
    :global(.bx--pagination__left > label.bx--pagination__text) {
    margin-right: 0;
    padding-right: var(--control-gap);
  }
  .company-framework-pagination
    :global(.bx--select__page-number + .bx--pagination__text) {
    margin-left: 0;
    padding-left: var(--control-gap);
  }
  .company-framework-pagination :global(.bx--pagination__text) {
    color: var(--pax-white);
  }
  .company-framework-pagination :global(.bx--select-input) {
    border-color: var(--pax-sub-new) !important;
    background: var(--pax-black);
    color: var(--pax-white);
  }
  .company-framework-pagination :global(.bx--select-input:hover),
  .company-framework-pagination :global(.bx--select-input:focus) {
    background: var(--pax-sub-new) !important;
  }
  .company-framework-pagination :global(.bx--select__arrow) {
    fill: var(--pax-white);
  }
  .company-framework-pagination :global(.bx--pagination__button) {
    border-left-color: var(--pax-sub-new);
    color: var(--pax-white);
  }
  .company-framework-pagination :global(.bx--pagination__button .bx--btn__icon),
  .company-framework-pagination
    :global(.bx--pagination__button .bx--btn__icon path) {
    fill: currentColor !important;
  }
  .company-framework-pagination :global(.bx--pagination__button:hover) {
    background: var(--pax-sub-new);
    color: var(--pax-white);
  }
  .company-framework-pagination :global(.bx--pagination__button:disabled) {
    background: var(--pax-black);
    color: var(--pax-grey);
  }
</style>
